// Encryption/Decryption Utility Functions - Using CryptoJS to implement AES-256-CBC encryption
// Enhanced with IV for better security, compatible with UniApp version

import CryptoJS from 'crypto-js'

// Secret key and IV for AES-256-CBC encryption
const SECRET_KEY = 'EquestrianAppSecretKey2024!'
const SECRET_IV = 'EquestrianAppIV2024!!'

/**
 * Encrypt string using AES-256-CBC algorithm
 * @param {string} str - String to encrypt
 * @returns {string} Encrypted base64 encoded string with CRYPT_ prefix
 */
export function encrypt(str) {
  if (!str || typeof str !== 'string') return ''
  
  try {
    const encrypted = CryptoJS.AES.encrypt(
      str,
      SECRET_KEY,
      {
        iv: SECRET_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    
    return 'CRYPT_' + encrypted.toString()
  } catch (error) {
    console.error('Encryption failed:', error)
    return ''
  }
}

/**
 * Decrypt string using AES-256-CBC algorithm
 * @param {string} str - String to decrypt
 * @returns {string} Decrypted original string
 */
export function decrypt(str) {
  if (!str || typeof str !== 'string') return ''
  
  // Check prefix
  if (!str.startsWith('CRYPT_')) return str
  
  try {
    const encryptedStr = str.substring(6)
    
    const decrypted = CryptoJS.AES.decrypt(
      encryptedStr,
      SECRET_KEY,
      {
        iv: SECRET_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    
    return decrypted.toString(CryptoJS.enc.Utf8)
  } catch (error) {
    console.error('Decryption failed:', error)
    return ''
  }
}

/**
 * Encrypt object and convert to string
 * @param {object} obj - Object to encrypt
 * @returns {string} Encrypted string
 */
export function encryptObject(obj) {
  try {
    if (obj === null || obj === undefined) {
      return encrypt(JSON.stringify(null))
    }
    const jsonString = JSON.stringify(obj)
    return encrypt(jsonString)
  } catch (error) {
    console.error('Object encryption failed:', error)
    return ''
  }
}

/**
 * Decrypt and parse encrypted object string
 * @param {string} encryptedStr - Encrypted string
 * @param {boolean} isTest - Whether called from test environment
 * @returns {object|null} Decrypted object, returns null if decryption fails
 */
export function decryptObject(encryptedStr, isTest = false) {
  try {
    // Input validation
    if (encryptedStr === null || encryptedStr === undefined || typeof encryptedStr !== 'string') {
      console.warn('decryptObject: Invalid input parameter')
      return null
    }
    
    const trimmedStr = encryptedStr.trim()
    if (trimmedStr === '') {
      console.warn('decryptObject: Empty string input')
      return null
    }
    
    // Decrypt string
    let decryptedStr
    try {
      decryptedStr = decrypt(trimmedStr)
      
      if (!decryptedStr || decryptedStr === '') {
        console.error('decryptObject: Decryption returned empty result')
        return null
      }
      
      // Parse JSON
      try {
        const parsedObj = JSON.parse(decryptedStr)
        
        if (typeof parsedObj !== 'object' || parsedObj === null) {
          console.error('decryptObject: Parse result is not valid object')
          return null
        }
        
        if (Array.isArray(parsedObj)) {
          console.error('decryptObject: Parse result is array, not object')
          return null
        }
        
        return parsedObj
      } catch (jsonError) {
        console.error('decryptObject: JSON parse failed:', jsonError.message)
        return null
      }
    } catch (decryptError) {
      console.error('decryptObject: Decryption error:', decryptError.message)
      return null
    }
  } catch (unexpectedError) {
    console.error('decryptObject: Unexpected error:', unexpectedError.message)
    return null
  }
}

export function hash(text) {
  try {
    return CryptoJS.SHA256(text).toString()
  } catch (error) {
    console.error('Hashing failed:', error)
    return null
  }
}

export function generateId() {
  return CryptoJS.lib.WordArray.random(16).toString()
}